/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, PropsWithChildren, useCallback, useRef, useState } from "react";

import { Key } from "ts-key-enum";
import { BEM } from "@adapptio/lib-components";
import { isArray, isFunction, isNil, isString, isUndefined } from "@adapptio/lib-shared";
import { FadeInOut } from "../FadeInOut";
import { FC_ANCHOR, FC_DIRECTION, FC_SIZE, FloatingContainer } from "../FloatingContainer";
import { MenuList } from "../MenuList";
import { ISelectInputProps, SelectInput } from "../SelectInput";
import { useEscKey, useStateProp } from "../../../hooks";
import { ClickAwayListener } from "../ClickAwayListener";

export interface ISelectOption_Item {
	id?: string;
	value?: string;
	label?: string;
	description?: string;
	icon?: string;
	onClick?: React.MouseEventHandler;
}

export interface ISelectOption_Group {
	title?: string;
	items: ISelectOption_Item[];
}

export interface ISelectOption_Separator {
	isSeparator: boolean;
}

export type TSelectOption = ISelectOption_Item | ISelectOption_Separator | ISelectOption_Group;

export function isSelectOptionSeparator(value: TSelectOption): value is ISelectOption_Separator {
	return !isNil(value) && value["isSeparator"];
}

export function isSelectOptionItem(value: TSelectOption): value is ISelectOption_Item {
	return (
		!isNil(value) &&
		!isSelectOptionSeparator(value) &&
		(!isUndefined(value["value"]) ||
			!isUndefined(value["label"]) ||
			!isUndefined("description") ||
			!isUndefined("icon"))
	);
}

export function isSelectOptionGroup(value: TSelectOption): value is ISelectOption_Group {
	return !isNil(value) && isArray(value["items"]);
}

function areEqualValues(a: unknown, b: unknown) {
	if (typeof b === "object" && b !== null) {
		return a === b;
	}

	return String(a) === String(b);
}

export interface ISelectProps {
	inputChildren?: React.ReactNode;
	value: string;
	id?: string;
	placeholder?: string;
	readOnly?: boolean;
	allowCustomInput?: boolean;
	className?: string;
	popupClassName?: string;
	onChange?: (value: string) => void;
	renderValue?: (value: string) => { value?: string; icon?: string } | string;
	displayedValue?: string;
	displayedIcon?: string;
	grow?: boolean;
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
	InputComponent?: React.ElementType<ISelectInputProps>;
	onOpen?: () => void;
	onClose?: () => void;
	open?: boolean;
}

export const Select: FC<PropsWithChildren<ISelectProps>> = ({
	children,
	inputChildren,
	value,
	placeholder,
	readOnly,
	allowCustomInput: comboBox,
	onChange,
	className,
	popupClassName,
	renderValue,
	displayedValue,
	displayedIcon,
	id,
	startAdornment,
	endAdornment,
	InputComponent,
	onOpen,
	onClose,
	open
}) => {
	const fieldControlRef = useRef<HTMLDivElement>();
	const inputRef = useRef<HTMLInputElement>();
	const containerRef = useRef();
	const [ focusOnListItem, setFocusOnListItem ] = useState(false);
	const [ dropdownOpen, setDropdownOpen ] = useStateProp(!!open);

	const childrenArray = React.Children.toArray(children);

	const renderedValue = isFunction(renderValue) ? renderValue(value) : null;

	const visibleValue =
		(!isNil(renderedValue)
			? isString(renderedValue)
				? renderedValue
				: renderedValue.value
			: displayedValue) || value;
	const visibleIcon =
		!isNil(renderedValue) && !isString(renderedValue) ? renderedValue.icon : displayedIcon;

	const baseClass = BEM("edt-select", null, {
		type: comboBox ? "combobox" : "select",
		open: dropdownOpen,
		readonly: readOnly
	});

	const openDropdown = useCallback(() => {
		if (isFunction(onOpen)) {
			onOpen();
			return;
		}

		setDropdownOpen(true);
	}, [ onOpen, setDropdownOpen ]);

	const closeDropdown = useCallback(() => {
		if (isFunction(onClose)) {
			onClose();
			return;
		}

		setDropdownOpen(false);
	}, [ onClose, setDropdownOpen ]);

	const tryOpenDropdown = useCallback(() => {
		if (readOnly) {
			return;
		}

		openDropdown();
	}, [ readOnly, openDropdown ]);

	const focusOnField = useCallback(() => {
		if (!isNil(inputRef.current)) {
			inputRef.current.focus();
			inputRef.current.selectionStart = inputRef.current?.value?.length;
			inputRef.current.selectionEnd = inputRef.current?.value?.length;
		}
	}, [ inputRef ]);

	const handleInputChange = useCallback<ISelectInputProps["onChange"]>(
		(event) => {
			if (!comboBox) {
				return;
			}

			if (!isUndefined((event.target as HTMLInputElement).value)) {
				const value = (event.target as HTMLInputElement).value;
				onChange(value);
			}
		},
		[ onChange ]
	);

	const handleInputKeyDown = useCallback<ISelectInputProps["onKeyDown"]>(
		(event) => {
			switch (event.key) {
				case Key.Enter:
				case Key.Tab:
					// might be selecting an option
					if (dropdownOpen) {
						event.preventDefault();
					}
					break;
				case Key.ArrowDown:
					event.preventDefault();

					if (!dropdownOpen) {
						tryOpenDropdown();
						break;
					}

					if (!focusOnListItem) {
						setFocusOnListItem(true);
					}
					break;
				case Key.ArrowUp:
					if (dropdownOpen) {
						closeDropdown();
						break;
					}
					break;
				case " ":
					if (!dropdownOpen) {
						tryOpenDropdown();
						event.preventDefault();
						break;
					}

					if (!comboBox) {
						closeDropdown();
						setFocusOnListItem(false);
					}

					break;
			}
		},
		[ dropdownOpen, openDropdown ]
	);

	const handleOnWrapperClick = useCallback<React.MouseEventHandler>(
		(event) => {
			if (readOnly) {
				return;
			}
			tryOpenDropdown();

			if (focusOnListItem) {
				setFocusOnListItem(false);
			}
		},
		[ tryOpenDropdown, focusOnListItem, setFocusOnListItem, readOnly ]
	);

	const handleOnTriggerClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
		(event) => {
			if (dropdownOpen) {
				if (focusOnListItem) {
					setFocusOnListItem(false);
				}
				return closeDropdown();
			}

			setFocusOnListItem(true);

			tryOpenDropdown();
		},
		[ openDropdown, closeDropdown, dropdownOpen, focusOnListItem, setFocusOnListItem ]
	);

	const handleItemClick = useCallback<(child: React.ReactNode) => React.MouseEventHandler<HTMLElement>>(
		(child) => (event) => {
			if (!React.isValidElement(child)) {
				return;
			}

			if (!event.currentTarget.hasAttribute("tabindex")) {
				return;
			}

			const newValue = child.props.value;

			if (isFunction(child.props.onClick)) {
				child.props.onClick(event);
			}

			if (newValue !== value) {
				if (isFunction(onChange)) {
					onChange(newValue);
				}
			}

			closeDropdown();
			focusOnField();
			setFocusOnListItem(false);
		},
		[ value, onChange, closeDropdown, setFocusOnListItem ]
	);

	const handleItemKeyUp = useCallback<(child: React.ReactNode) => React.KeyboardEventHandler<HTMLElement>>(
		(child) => (event) => {
			if (!React.isValidElement(child)) {
				return;
			}

			if (event.key === " ") {
				event.preventDefault();
			}

			if (isFunction(child.props.onKeyUp)) {
				child.props.onKeyUp(event);
			}
		},
		[]
	);

	const handleItemKeyDown = useCallback<React.KeyboardEventHandler<HTMLElement>>((event) => {
		if (event.key === Key.ArrowUp && !event.currentTarget.previousSibling) {
			closeDropdown();
			setFocusOnListItem(false);
			focusOnField();
		}
	}, []);

	const handleClose = useCallback<React.MouseEventHandler<HTMLDivElement>>(
		(event) => {
			if (event.target !== event.currentTarget) {
				return;
			}

			if (isFunction(onClose)) {
				onClose();
			}

			console.log({ event });

			if (!event.defaultPrevented) {
				closeDropdown();
			}
		},
		[ closeDropdown, onClose ]
	);

	useEscKey(() => {
		closeDropdown();
	}, dropdownOpen);

	const items = childrenArray.map((child) => {
		if (!React.isValidElement(child)) {
			return null;
		}

		const selected = areEqualValues(child.props.value, value);

		return React.cloneElement(child, {
			"aria-selected": `${selected}`,
			selected,
			onClick: handleItemClick(child),
			onKeyUp: handleItemKeyUp(child),
			onKeyDown: handleItemKeyDown,
			role: "option",
			tabIndex: child.props.tabIndex || (selected ? 0 : -1),
			value: undefined, // most likely not valid HTML attribute,
			"data-value": child.props.value
		} as React.HTMLAttributes<unknown>);
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<>
			<div
				className={baseClass.element("wrapper").toClassName()}
				role="button"
				aria-haspopup="listbox"
				aria-expanded={dropdownOpen}
				onClick={handleOnWrapperClick}
				ref={fieldControlRef}
			>
				<InputComponent
					id={id}
					open={dropdownOpen}
					inputRef={inputRef}
					preventEdit={!comboBox}
					className={baseClass.toClassName()}
					placeholder={placeholder}
					value={visibleValue}
					onChange={handleInputChange}
					onKeyDown={handleInputKeyDown}
					icon={visibleIcon}
					startAdornment={startAdornment}
					endAdornment={endAdornment}
					onTriggerClick={handleOnTriggerClick}
				>
					{inputChildren}
				</InputComponent>
			</div>

			<FadeInOut
				isVisible={dropdownOpen}
				fadeOutDelay={250}
				render={(isVisible) => {
					const dropdownClass = BEM("edt-select-popup", null, {
						type: comboBox ? "combobox" : "select",
						expanded: dropdownOpen,
						visible: isVisible
					});

					if (className) {
						dropdownClass.add(className);
					}

					if (popupClassName) {
						dropdownClass.add(popupClassName);
					}

					return (
						<>
							<ClickAwayListener onClickAway={closeDropdown}>
								<FloatingContainer
									ref={containerRef}
									parent={fieldControlRef}
									anchor={FC_ANCHOR.CENTER_BOTTOM}
									direction={FC_DIRECTION.HR}
									size={FC_SIZE.FIT_MIN}
									className={dropdownClass.toClassName()}
									onClose={handleClose}
								>
									<MenuList
										role="listbox"
										autoFocusItem={focusOnListItem}
										className={dropdownClass.element("options").toClassName()}
										disableListWrap
										variant="selectedMenu"
									>
										{items}
									</MenuList>
								</FloatingContainer>
							</ClickAwayListener>
						</>
					);
				}}
			/>
		</>
	);
};

Select.defaultProps = {
	InputComponent: SelectInput
};
